<!-- ──────────────────── Connector picker (always visible) ─────────────────── -->
<div class="p-fluid mb-4">
  <div class="field">
    <label for="connector">Select Connector</label>
    <p-select
      inputId="connector"
      class="w-full"
      appendTo="body"
      [options]="connectors"
      optionLabel="name"
      optionValue="id"
      [(ngModel)]="selectedConnectorId"
      (ngModelChange)="onConnectorChange()"
      placeholder="Choose a connector"
    ></p-select>
  </div>
</div>

<!-- ───────────────────── Demo-loan browser ────────────────────── -->
<app-demo-loan-browser *ngIf="isDemoSelected" (selected)="selectedDemoLoan = $event"></app-demo-loan-browser>

<!-- ───────────────────── Regular connector search UI ────────────────────── -->
<div *ngIf="!isDemoSelected" class="p-fluid">
  <!-- search-type picker -->
  <div class="field" *ngIf="!isMongoSelected">
    <label for="searchType">Search By</label>
    <p-select
      inputId="searchType"
      class="w-full"
      [options]="[
        { label: 'Display ID', value: 'displayId' },
        { label: 'System ID', value: 'systemId' },
        { label: 'System ID Range', value: 'systemIdRange' },
      ]"
      [(ngModel)]="searchType"
      placeholder="Select Search Type"
    ></p-select>
  </div>

  <!-- single-value search -->
  <div class="field" *ngIf="searchType === 'displayId' || searchType === 'systemId'">
    <label for="searchValue">Search Value</label>
    <input
      *ngIf="searchType === 'displayId'"
      id="searchValue"
      pInputText
      class="w-full"
      placeholder="Enter a Display ID"
      [(ngModel)]="searchValue"
      [ngModelOptions]="{ standalone: true }"
      [attr.key]="selectedConnectorId + '-' + searchType"
    />
    <p-inputnumber
      *ngIf="searchType === 'systemId'"
      inputId="searchValue"
      class="w-full"
      [useGrouping]="false"
      mode="decimal"
      placeholder="Enter a System ID"
      [(ngModel)]="searchValue"
      [ngModelOptions]="{ standalone: true }"
      [attr.key]="selectedConnectorId + '-' + searchType"
    ></p-inputnumber>
  </div>

  <!-- range search -->
  <div class="formgrid grid" *ngIf="searchType === 'systemIdRange'">
    <div class="field col">
      <label for="fromSystemId">From System ID</label>
      <input
        id="fromSystemId"
        pInputText
        class="w-full"
        placeholder="Enter starting System ID"
        [(ngModel)]="fromSystemId"
      />
    </div>
    <div class="field col">
      <label for="toSystemId">To System ID</label>
      <input id="toSystemId" pInputText class="w-full" placeholder="Enter ending System ID" [(ngModel)]="toSystemId" />
    </div>
  </div>

  <!-- progress indicator (range mode only) -->
  <div class="field" *ngIf="isLoading && searchType === 'systemIdRange'">
    <p-progressbar [value]="progressValue" showValue="true">
      <ng-template #content>{{ progressValue }}% ({{ loansLoaded }} / {{ totalLoans }})</ng-template>
    </p-progressbar>
  </div>
</div>

<!-- ───────────────────── Action buttons (always visible) ─────────────────── -->
<div class="flex gap-2 mt-4 justify-content-end">
  <!-- Demo mode: single import button -->
  <p-button
    *ngIf="isDemoSelected"
    label="Import Selected Loan"
    icon="pi pi-cloud-download"
    [disabled]="!selectedDemoLoan"
    (click)="importDemoLoan()"
    [loading]="isLoading"
    severity="success"
  ></p-button>

  <!-- Non-demo mode: preview + import -->
  <ng-container *ngIf="!isDemoSelected">
    <p-button label="Preview" icon="pi pi-eye" (click)="onPreview()" [loading]="isLoading" severity="info"></p-button>

    <p-button
      label="Import Loan(s)"
      icon="pi pi-cloud-download"
      (click)="importLoan()"
      [loading]="isLoading"
    ></p-button>
  </ng-container>
</div>

<!-- Preview Dialog -->
<p-dialog
  header="Loan(s) Preview"
  [(visible)]="showPreviewDialog"
  [modal]="true"
  appendTo="body"
  resizable="true"
  maximizable="true"
  [style]="{ width: '80%', minHeight: '50vh' }"
  (onHide)="showPreviewDialog = false"
>
  <div *ngIf="isLoading" class="text-center p-4">
    <p-progressSpinner></p-progressSpinner>
  </div>

  <div *ngIf="!isLoading">
    <p *ngIf="errorMsg" class="text-center" style="color: red">
      {{ errorMsg }}
    </p>
    <!-- If single loan -->
    <div *ngIf="previewLoans.length === 1 && !errorMsg">
      <h3>Loan Details</h3>
      <div class="grid">
        <div class="col-6">
          <strong>Loan Name:</strong>
          {{ previewLoans[0].d.displayId }}
        </div>
        <div class="col-6">
          <strong>Principal:</strong>
          {{ previewLoans[0].d.LoanSetup.tilLoanAmount }}
        </div>
        <div class="col-6 mt-2">
          <strong>Start Date:</strong>
          {{ previewLoans[0].d.LoanSetup.contractDate | date: 'yyyy-MM-dd' }}
        </div>
        <div class="col-6 mt-2">
          <strong>Rate:</strong>
          {{ previewLoans[0].d.LoanSetup.loanRate }}%
        </div>
      </div>

      <h4 class="mt-4">Payments</h4>
      <p-table [value]="previewLoans[0].d.Payments" [paginator]="true" [rows]="5">
        <ng-template pTemplate="header">
          <tr>
            <th>Date</th>
            <th>Amount</th>
            <th>Info</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-payment>
          <tr>
            <td>{{ payment.date | date: 'yyyy-MM-dd' }}</td>
            <td>{{ payment.amount }}</td>
            <td>{{ payment.info }}</td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- If multiple loans -->
    <div *ngIf="previewLoans.length > 1 && !errorMsg">
      <h3>Found {{ previewLoans.length }} Loans:</h3>
      <p-table [value]="previewLoans" [paginator]="true" [rows]="10">
        <ng-template pTemplate="header">
          <tr>
            <th>Loan ID</th>
            <th>Display ID</th>
            <th>Principal</th>
            <th>Start Date</th>
            <th>Rate</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-l>
          <tr>
            <td>{{ l.d.id }}</td>
            <td>{{ l.d.displayId }}</td>
            <td>{{ l.d.LoanSetup.tilLoanAmount }}</td>
            <td>{{ l.d.LoanSetup.contractDate | date: 'yyyy-MM-dd' }}</td>
            <td>{{ l.d.LoanSetup.loanRate }}%</td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <p-footer>
    <p-button label="Close" class="p-button-text" (click)="showPreviewDialog = false"></p-button>
  </p-footer>
</p-dialog>
